import { useEffect, useRef, useState } from 'react'
import { LANGUAGES, getLanguage, useI18n, type LanguageCode } from '../i18n'
import { Flag } from './Flag'
import { Icon } from './Icon'
import { Text } from './Text'

/**
 * Language switcher for the foot of the login screen: a pill showing the active
 * language (flag + abbreviation + chevron) that opens a menu of all languages,
 * each with its flag and abbreviation, a check on the active one.
 *
 * The menu opens UPWARD — the pill lives at the bottom of the screen — which is
 * why the chevron points up when closed and flips down while the menu is open.
 * Closes on outside pointer, Escape, or selection.
 *
 * `menuAlign` controls how the dropdown lines up with the pill: 'center' (default)
 * centers it over the pill — right for a centered pill (login/prequal); 'end'
 * pins its right edge to the pill — right for a pill pushed to the screen's right
 * edge (home), where centering would overflow off-screen.
 */
export function LanguageSwitcher({
  className,
  menuAlign = 'center',
}: {
  className?: string
  menuAlign?: 'center' | 'end'
}) {
  const { locale, setLocale, t } = useI18n()
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  const active = getLanguage(locale)
  const menuAlignClass =
    menuAlign === 'end' ? 'right-0' : 'left-1/2 -translate-x-1/2'

  useEffect(() => {
    if (!open) return

    function handlePointerDown(event: PointerEvent) {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false)
    }
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false)
    }

    document.addEventListener('pointerdown', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [open])

  function select(code: LanguageCode) {
    setLocale(code)
    setOpen(false)
  }

  return (
    <div ref={rootRef} className={`relative inline-block ${className ?? ''}`}>
      {open && (
        <ul
          role="listbox"
          aria-label={t('language.label')}
          className={`absolute bottom-full mb-3 w-31 overflow-hidden rounded-2xl border border-gray-200 bg-white py-2 shadow-[0_10px_30px_rgba(0,0,0,0.12)] ${menuAlignClass}`}
        >
          {LANGUAGES.map((language) => {
            const selected = language.code === locale
            return (
              <li key={language.code} role="option" aria-selected={selected}>
                <button
                  type="button"
                  onClick={() => select(language.code)}
                  className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors active:bg-gray-50 focus-visible:bg-gray-50 focus-visible:outline-none"
                >
                  <Flag code={language.code} className="size-7" />
                  <Text
                    variant="base"
                    weight={selected ? 'semibold' : 'normal'}
                    className="flex-1 text-gray-900"
                  >
                    {language.abbr}
                  </Text>
                  {selected && <Icon name="check" className="size-8" />}
                </button>
              </li>
            )
          })}
        </ul>
      )}

      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={t('language.label')}
        className="inline-flex h-10 select-none items-center gap-2 rounded-full border border-gray-200 bg-white pl-4 pr-3 shadow-[0_5px_20px_rgba(0,0,0,0.10)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
      >
        <Flag code={locale} className="size-6" />
        <Text variant="base" weight="semibold" className="text-gray-900">
          {active.abbr}
        </Text>
        <Icon name={open ? 'chevron-down' : 'chevron-up'} className="size-6" />
      </button>
    </div>
  )
}
